import React, { useCallback, useEffect, useState, Component } from 'react';
import { CheckCircle2, XCircle, Shield } from 'lucide-react';
const TOTAL_STEPS = 10; // 5 auth + 5 return
const CYCLE_INTERVAL = 2000; // 2 seconds
export function HowItWorksTab() {
  const [showTechnical, setShowTechnical] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  // Auto-cycle through steps
  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setActiveStep(prev => (prev + 1) % TOTAL_STEPS);
    }, CYCLE_INTERVAL);
    return () => clearInterval(timer);
  }, [isPaused]);
  const handleStepHover = useCallback((index: number) => {
    setIsPaused(true);
    setActiveStep(index);
  }, []);
  const handleStepLeave = useCallback(() => {
    setIsPaused(false);
  }, []);
  return <div className="max-w-3xl mx-auto py-12 px-4" data-id="element-105">
      {/* SECTION 1: DELEGATED AUTHORIZATION */}
      <div className="text-center mb-8" data-id="element-106">
        <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-107">
          Delegated Authorization
        </h2>
        <p className="text-lg text-gray-600 mb-6" data-id="element-108">
          MCPGuard authorizes every AI action against both the human and the AI
          agent before it reaches an MCP.
        </p>

        {/* Shared Technical Toggle */}
        <div className="flex justify-center md:justify-end w-full max-w-2xl mx-auto" data-id="element-109">
          <button onClick={() => setShowTechnical(!showTechnical)} className="group flex items-center space-x-3 focus:outline-none" aria-pressed={showTechnical} data-id="element-110">
            <span className={`text-sm font-medium transition-colors ${showTechnical ? 'text-blue-700' : 'text-gray-500 group-hover:text-gray-700'}`} data-id="element-111">
              🔧 Show technical details
            </span>
            <div className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${showTechnical ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-112">
              <span aria-hidden="true" className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${showTechnical ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-113" />
            </div>
          </button>
        </div>
      </div>

      {/* Authorization Steps */}
      <div className="relative max-w-2xl mx-auto mb-20" data-id="element-114">
        <div className="absolute left-8 top-0 bottom-0 w-px bg-gray-200 hidden md:block" data-id="element-115" />

        <div className="space-y-12" data-id="element-116">
          {/* Step 1 */}
          <AuthStep number={1} title="A human requests an action" showTechnical={showTechnical} isHighlighted={activeStep === 0} onHover={() => handleStepHover(0)} onLeave={handleStepLeave} data-id="element-117">
            <TechnicalCode content={`Input:\n  - human_id\n  - tenant_id\n  - session_id\n  - requested_action\n  - target_mcp`} data-id="element-118" />
            <TechnicalExplanation text="The request originates from an authenticated human session. Identity and tenant context are attached before any agent execution occurs." data-id="element-119" />
          </AuthStep>

          {/* Step 2 */}
          <AuthStep number={2} title="An AI agent attempts execution" showTechnical={showTechnical} isHighlighted={activeStep === 1} onHover={() => handleStepHover(1)} onLeave={handleStepLeave} data-id="element-120">
            <TechnicalCode content={`Agent Context:\n  - agent_id\n  - agent_installation_id\n  - auth_method (OIDC / JWT / API key)\n  - agent_scopes`} data-id="element-121" />
            <TechnicalExplanation text="The AI agent does not inherit human permissions. It presents its own identity and credentials when attempting the action." data-id="element-122" />
          </AuthStep>

          {/* Step 3 */}
          <AuthStep number={3} title="MCPGuard evaluates human and agent permissions" showTechnical={showTechnical} isHighlighted={activeStep === 2} onHover={() => handleStepHover(2)} onLeave={handleStepLeave} data-id="element-123">
            <TechnicalCode content={`Authorization Check:\n  allow =\n    policy.allows(human, action, mcp)\n    AND\n    policy.allows(agent, action, mcp)`} data-id="element-124" />
            <TechnicalExplanation text="MCPGuard evaluates a dual-principal authorization decision. Both the human and the agent must independently be permitted." data-id="element-125" />
          </AuthStep>

          {/* Step 4 */}
          <AuthStep number={4} title="Policy is enforced in real time" showTechnical={showTechnical} isHighlighted={activeStep === 3} onHover={() => handleStepHover(3)} onLeave={handleStepLeave} data-id="element-126">
            <TechnicalCode content={`Policy Inputs:\n  - identities (human + agent)\n  - action\n  - MCP\n  - environment\n  - runtime context`} data-id="element-127" />
            <TechnicalExplanation text="Policies are evaluated synchronously before the MCP is called. There is no post-hoc enforcement and no background correction." data-id="element-128" />
          </AuthStep>

          {/* Step 5 — Decision Step */}
          <div className="relative" onMouseEnter={() => handleStepHover(4)} onMouseLeave={handleStepLeave} data-id="element-129">
            <div className={`flex items-start group rounded-xl px-4 py-3 -mx-4 transition-all duration-500 ${activeStep === 4 ? 'bg-blue-50/60' : ''}`} data-id="element-130">
              <StepCircle number={5} isHighlighted={activeStep === 4} data-id="element-131" />
              <div className="pt-4 w-full" data-id="element-132">
                <p className={`text-xl font-medium transition-colors duration-500 ${activeStep === 4 ? 'text-blue-900' : 'text-gray-900'}`} data-id="element-133">
                  The action is allowed or denied
                </p>

                {showTechnical && <div className="mt-6 border-l-2 border-blue-100 pl-6 -ml-2 space-y-6" data-id="element-134">
                    {/* Allowed */}
                    <div data-id="element-135">
                      <div className="flex items-center space-x-2 mb-2" data-id="element-136">
                        <CheckCircle2 className="h-4 w-4 text-green-600" data-id="element-137" />
                        <span className="text-sm font-semibold text-gray-900" data-id="element-138">
                          If allowed
                        </span>
                      </div>
                      <TechnicalCode content={`Response:\n  - mcp_response\n  - decision = allow\n  - decision_id\n  - audit_reference`} data-id="element-139" />
                      <TechnicalExplanation text="The MCP response is returned along with authorization metadata and an audit reference." data-id="element-140" />
                    </div>

                    {/* Denied */}
                    <div data-id="element-141">
                      <div className="flex items-center space-x-2 mb-2" data-id="element-142">
                        <XCircle className="h-4 w-4 text-red-600" data-id="element-143" />
                        <span className="text-sm font-semibold text-gray-900" data-id="element-144">
                          If denied
                        </span>
                      </div>
                      <TechnicalCode content={`Response:\n  - decision = deny\n  - reason_code\n  - audit_reference`} data-id="element-145" />
                      <TechnicalExplanation text="The MCP is never called. A structured denial is returned with an auditable reason." data-id="element-146" />
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SEPARATOR */}
      <div className="max-w-2xl mx-auto" data-id="element-147">
        <div className="border-t border-gray-200" data-id="element-148" />
      </div>

      {/* SECTION 2: CONTROLLED RETURN FLOW */}
      <div className="text-center mt-16 mb-16" data-id="element-149">
        <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-150">
          Controlled Return Flow
        </h2>
        <div className="max-w-2xl mx-auto text-lg text-gray-600 space-y-2" data-id="element-151">
          <p data-id="element-152">
            When an action is allowed, MCPGuard governs what data is returned
            from the MCP before it is sent back to the AI agent.
          </p>
          <p data-id="element-153">
            Sensitive data is minimized, redacted, or withheld based on policy —
            even when the action itself is permitted.
          </p>
        </div>
      </div>

      {/* Return Flow Steps */}
      <div className="relative max-w-2xl mx-auto" data-id="element-154">
        <div className="absolute left-8 top-0 bottom-0 w-px bg-gray-200 hidden md:block" data-id="element-155" />

        <div className="space-y-12" data-id="element-156">
          {/* Return Step 1 */}
          <ReturnStep number={1} title="MCP executes the approved action" isHighlighted={activeStep === 5} onHover={() => handleStepHover(5)} onLeave={handleStepLeave} data-id="element-157" />

          {/* Return Step 2 */}
          <ReturnStep number={2} title="MCP returns a response payload" isHighlighted={activeStep === 6} onHover={() => handleStepHover(6)} onLeave={handleStepLeave} data-id="element-158" />

          {/* Return Step 3 — Policy Evaluation */}
          <div className="relative" onMouseEnter={() => handleStepHover(7)} onMouseLeave={handleStepLeave} data-id="element-159">
            <div className={`flex items-start group rounded-xl px-4 py-3 -mx-4 transition-all duration-500 ${activeStep === 7 ? 'bg-blue-50/60' : ''}`} data-id="element-160">
              <StepCircle number={3} isHighlighted={activeStep === 7} data-id="element-161" />
              <div className="pt-4 w-full" data-id="element-162">
                <p className={`text-xl font-medium transition-colors duration-500 ${activeStep === 7 ? 'text-blue-900' : 'text-gray-900'}`} data-id="element-163">
                  MCPGuard evaluates the response against return policies
                </p>

                {showTechnical && <div className="mt-6 border-l-2 border-blue-100 pl-6 -ml-2 space-y-3" data-id="element-164">
                    <h4 className="text-sm font-semibold text-gray-800" data-id="element-165">
                      Return Policy Evaluation
                    </h4>
                    <ul className="list-disc pl-4 space-y-1 text-sm text-gray-600" data-id="element-166">
                      <li data-id="element-167">Evaluated after MCP execution</li>
                      <li data-id="element-168">
                        Based on:
                        <ul className="list-disc pl-4 mt-1 space-y-1" data-id="element-169">
                          <li data-id="element-170">Human entitlements</li>
                          <li data-id="element-171">Agent permissions</li>
                          <li data-id="element-172">MCP classification</li>
                          <li data-id="element-173">Detected data categories</li>
                          <li data-id="element-174">Runtime context</li>
                        </ul>
                      </li>
                    </ul>
                    <p className="text-sm text-gray-500 mt-2" data-id="element-175">
                      Return policies are evaluated independently from request
                      authorization. An action may be allowed while its response
                      is still governed.
                    </p>
                  </div>}
              </div>
            </div>
          </div>

          {/* Return Step 4 — Outcomes & Samples */}
          <div className="relative" onMouseEnter={() => handleStepHover(8)} onMouseLeave={handleStepLeave} data-id="element-176">
            <div className={`flex items-start group rounded-xl px-4 py-3 -mx-4 transition-all duration-500 ${activeStep === 8 ? 'bg-blue-50/60' : ''}`} data-id="element-177">
              <StepCircle number={4} isHighlighted={activeStep === 8} data-id="element-178" />
              <div className="pt-4 w-full" data-id="element-179">
                <p className={`text-xl font-medium transition-colors duration-500 ${activeStep === 8 ? 'text-blue-900' : 'text-gray-900'}`} data-id="element-180">
                  Sensitive data is redacted or minimized as required
                </p>

                {showTechnical && <div className="mt-6 border-l-2 border-blue-100 pl-6 -ml-2 space-y-6" data-id="element-181">
                    <div data-id="element-182">
                      <h4 className="text-sm font-semibold text-gray-800 mb-2" data-id="element-183">
                        Possible Outcomes
                      </h4>
                      <ul className="list-disc pl-4 space-y-1 text-sm text-gray-600" data-id="element-184">
                        <li data-id="element-185">Full response returned</li>
                        <li data-id="element-186">Redacted response returned</li>
                        <li data-id="element-187">Summary returned</li>
                        <li data-id="element-188">Return blocked (action allowed, data withheld)</li>
                      </ul>
                    </div>

                    <div data-id="element-189">
                      <div className="flex items-center space-x-2 mb-2" data-id="element-190">
                        <CheckCircle2 className="h-4 w-4 text-green-600" data-id="element-191" />
                        <span className="text-sm font-semibold text-gray-900" data-id="element-192">
                          If the response is allowed in full
                        </span>
                      </div>
                      <ul className="list-disc pl-4 space-y-1 text-sm text-gray-600 bg-slate-50 rounded-md p-3 border border-slate-100" data-id="element-193">
                        <li data-id="element-194">MCP response payload is returned</li>
                        <li data-id="element-195">Authorization metadata is attached</li>
                        <li data-id="element-196">Audit reference ID is included</li>
                      </ul>
                    </div>

                    <div data-id="element-197">
                      <div className="flex items-center space-x-2 mb-2" data-id="element-198">
                        <Shield className="h-4 w-4 text-amber-500" data-id="element-199" />
                        <span className="text-sm font-semibold text-gray-900" data-id="element-200">
                          If sensitive data is detected
                        </span>
                      </div>
                      <ul className="list-disc pl-4 space-y-1 text-sm text-gray-600 bg-slate-50 rounded-md p-3 border border-slate-100" data-id="element-201">
                        <li data-id="element-202">Sensitive fields are removed or masked</li>
                        <li data-id="element-203">Non-sensitive data is preserved</li>
                        <li data-id="element-204">Redaction reason codes are attached</li>
                      </ul>
                    </div>

                    <div data-id="element-205">
                      <div className="flex items-center space-x-2 mb-2" data-id="element-206">
                        <XCircle className="h-4 w-4 text-red-600" data-id="element-207" />
                        <span className="text-sm font-semibold text-gray-900" data-id="element-208">
                          If the response cannot be returned
                        </span>
                      </div>
                      <ul className="list-disc pl-4 space-y-1 text-sm text-gray-600 bg-slate-50 rounded-md p-3 border border-slate-100" data-id="element-209">
                        <li data-id="element-210">MCP is not re-called</li>
                        <li data-id="element-211">A structured denial is returned to the agent</li>
                        <li data-id="element-212">An audit reference is provided</li>
                      </ul>
                    </div>
                  </div>}
              </div>
            </div>
          </div>

          {/* Return Step 5 — Final */}
          <div className="relative" onMouseEnter={() => handleStepHover(9)} onMouseLeave={handleStepLeave} data-id="element-213">
            <div className={`flex items-start group rounded-xl px-4 py-3 -mx-4 transition-all duration-500 ${activeStep === 9 ? 'bg-blue-50/60' : ''}`} data-id="element-214">
              <StepCircle number={5} isHighlighted={activeStep === 9} data-id="element-215" />
              <div className="pt-4 w-full" data-id="element-216">
                <p className={`text-xl font-medium transition-colors duration-500 ${activeStep === 9 ? 'text-blue-900' : 'text-gray-900'}`} data-id="element-217">
                  A governed response is returned to the AI agent
                </p>

                {showTechnical && <div className="mt-6 border-l-2 border-blue-100 pl-6 -ml-2 space-y-3" data-id="element-218">
                    <h4 className="text-sm font-semibold text-gray-800" data-id="element-219">
                      What the AI Agent Receives
                    </h4>
                    <ul className="list-disc pl-4 space-y-1 text-sm text-gray-600" data-id="element-220">
                      <li data-id="element-221">
                        Governed response payload (full, redacted, or
                        summarized)
                      </li>
                      <li data-id="element-222">Decision outcome</li>
                      <li data-id="element-223">Audit reference ID</li>
                      <li data-id="element-224">Redaction indicators (when applicable)</li>
                    </ul>
                    <p className="text-sm font-medium text-gray-700 mt-2 italic" data-id="element-225">
                      The AI agent never receives raw data beyond its permitted
                      visibility.
                    </p>
                  </div>}
              </div>
            </div>
          </div>
        </div>

        {/* Global Audit Callout */}
        {showTechnical && <div className="mt-16" data-id="element-226">
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-5" data-id="element-227">
              <div className="flex items-start space-x-3 mb-3" data-id="element-228">
                <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-229" />
                <h4 className="text-sm font-bold text-blue-900" data-id="element-230">
                  Audit &amp; Traceability
                </h4>
              </div>
              <ul className="list-disc pl-12 space-y-1 text-sm text-blue-800 mb-3" data-id="element-231">
                <li data-id="element-232">Original request context</li>
                <li data-id="element-233">MCP called</li>
                <li data-id="element-234">Return policy applied</li>
                <li data-id="element-235">Redaction actions taken</li>
                <li data-id="element-236">Final response delivered</li>
              </ul>
              <p className="text-sm text-blue-900 font-medium pl-8 border-t border-blue-200 pt-3 mt-2" data-id="element-237">
                This completes the full audit chain: human → agent → policy →
                MCP → response → governance → delivery
              </p>
            </div>
          </div>}
      </div>
    </div>;
}
/* ─── Helper Components ─── */
function StepCircle({
  number,
  isHighlighted
}: {
  number: number;
  isHighlighted: boolean;
  active?: boolean;
}) {
  return <div className="flex-shrink-0 mr-6 relative z-10" data-id="element-238">
      <div className={`flex items-center justify-center w-16 h-16 rounded-full border-2 transition-all duration-500 shadow-sm ${isHighlighted ? 'bg-blue-600 border-blue-600 shadow-lg shadow-blue-200' : 'bg-white border-blue-100 group-hover:border-blue-500'}`} data-id="element-239">
        <span className={`text-xl font-bold transition-colors duration-500 ${isHighlighted ? 'text-white' : 'text-blue-600'}`} data-id="element-240">
          {number}
        </span>
      </div>
    </div>;
}
function TechnicalCode({
  content
}: {
  content: string;
}) {
  return <div className="bg-slate-50 rounded-md border border-slate-100 p-4 font-mono text-sm text-slate-700 whitespace-pre-wrap" data-id="element-241">
      {content}
    </div>;
}
function TechnicalExplanation({
  text
}: {
  text: string;
}) {
  return <p className="mt-2 text-sm text-gray-500 leading-relaxed" data-id="element-242">{text}</p>;
}
function AuthStep({
  number,
  title,
  showTechnical,
  isHighlighted,
  onHover,
  onLeave,
  children
}: {
  number: number;
  title: string;
  showTechnical: boolean;
  isHighlighted: boolean;
  onHover: () => void;
  onLeave: () => void;
  children: React.ReactNode;
}) {
  return <div className="relative" onMouseEnter={onHover} onMouseLeave={onLeave} data-id="element-243">
      <div className={`flex items-start group rounded-xl px-4 py-3 -mx-4 transition-all duration-500 ${isHighlighted ? 'bg-blue-50/60' : ''}`} data-id="element-244">
        <StepCircle number={number} isHighlighted={isHighlighted} data-id="element-245" />
        <div className="pt-4 w-full" data-id="element-246">
          <p className={`text-xl font-medium transition-colors duration-500 ${isHighlighted ? 'text-blue-900' : 'text-gray-900'}`} data-id="element-247">
            {title}
          </p>
          {showTechnical && <div className="mt-6 border-l-2 border-blue-100 pl-6 -ml-2" data-id="element-248">
              {children}
            </div>}
        </div>
      </div>
    </div>;
}
function ReturnStep({
  number,
  title,
  isHighlighted,
  onHover,
  onLeave
}: {
  number: number;
  title: string;
  isHighlighted: boolean;
  onHover: () => void;
  onLeave: () => void;
}) {
  return <div className="relative" onMouseEnter={onHover} onMouseLeave={onLeave} data-id="element-249">
      <div className={`flex items-start group rounded-xl px-4 py-3 -mx-4 transition-all duration-500 ${isHighlighted ? 'bg-blue-50/60' : ''}`} data-id="element-250">
        <StepCircle number={number} isHighlighted={isHighlighted} data-id="element-251" />
        <div className="pt-4 w-full" data-id="element-252">
          <p className={`text-xl font-medium transition-colors duration-500 ${isHighlighted ? 'text-blue-900' : 'text-gray-900'}`} data-id="element-253">
            {title}
          </p>
        </div>
      </div>
    </div>;
}